import http from '../shared/HttpCommon';

export interface TrustpilotTokenStatus {
  present: boolean;
  email?: string;
  consumerId?: string;
  expiresAt?: string;
}

export interface StartSessionResponse {
  sessionId: string;
  reference: string;
}

export interface SubmitCodeResponse {
  email: string;
  consumerId: string;
  expiresAt: string;
}

export interface SessionStatusResponse {
  status: 'pending' | 'code_sent' | 'error';
  message?: string;
  reason?: string;
  step?: string;
}

// Mirrors the backend's failure reasons; each has a trustpilot_token_failure_<reason> translation.
export const TRUSTPILOT_FAILURE_REASONS = [
  'browser_unavailable',
  'trustpilot_unavailable',
  'code_step_not_reached',
  'code_rejected',
  'unexpected',
] as const;

type TrustpilotFailureReason = (typeof TRUSTPILOT_FAILURE_REASONS)[number];

export const failureMessageKey = (
  reason?: string
): `trustpilot_token_failure_${TrustpilotFailureReason}` => {
  const known = TRUSTPILOT_FAILURE_REASONS.find((candidate) => candidate === reason);
  return `trustpilot_token_failure_${known ?? 'unexpected'}`;
};

// The backend's login steps up to the code field, in order; each has a trustpilot_token_progress_<step>
// translation describing what happens next.
export const TRUSTPILOT_PROGRESS_STEPS = [
  'browser_connected',
  'trustpilot_loaded',
  'login_opened',
  'email_submitted',
] as const;

export type TrustpilotProgressStep = (typeof TRUSTPILOT_PROGRESS_STEPS)[number];

// One more than the steps: before the first step is reached, the browser is still starting.
export const TRUSTPILOT_PROGRESS_TOTAL = TRUSTPILOT_PROGRESS_STEPS.length + 1;

export const isProgressStep = (step?: string): step is TrustpilotProgressStep =>
  TRUSTPILOT_PROGRESS_STEPS.some((candidate) => candidate === step);

export const progressNumber = (step?: TrustpilotProgressStep): number =>
  step ? TRUSTPILOT_PROGRESS_STEPS.indexOf(step) + 2 : 1;

export const progressMessageKey = (
  step?: TrustpilotProgressStep
): `trustpilot_token_progress_${TrustpilotProgressStep | 'started'}` =>
  `trustpilot_token_progress_${step ?? 'started'}`;

const getStatus = async (): Promise<TrustpilotTokenStatus | null> => {
  try {
    const { data } = await http.get('/trustpilot-token');
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const startSession = async (email: string): Promise<StartSessionResponse> => {
  const { data } = await http.post('/trustpilot-token/session/start', { email });
  return data;
};

const submitCode = async (sessionId: string, code: string): Promise<SubmitCodeResponse> => {
  const { data } = await http.post('/trustpilot-token/session/submit', { sessionId, code });
  return data;
};

const getSessionStatus = async (sessionId: string): Promise<SessionStatusResponse> => {
  const { data } = await http.get(`/trustpilot-token/session/${sessionId}/status`);
  return data;
};

const TrustpilotTokenService = {
  getStatus,
  startSession,
  submitCode,
  getSessionStatus,
};

export default TrustpilotTokenService;
